import DOMPurify from 'dompurify';

let linkTargetHookRegistered = false;

function registerLinkTargetHook(): void {
  if (linkTargetHookRegistered || typeof DOMPurify.addHook !== 'function') return;
  linkTargetHookRegistered = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName !== 'A') return;
    if (node.getAttribute('target') !== '_blank') return;
    const rel = node.getAttribute('rel') || '';
    if (!/\bnoopener\b/i.test(rel)) {
      node.setAttribute('rel', rel ? `${rel} noopener noreferrer`.trim() : 'noopener noreferrer');
    }
  });
}

/**
 * Sanitizes rich HTML for blog posts (admin save + public render).
 * Allows structure and safe inline styles from Word/Docs paste while blocking scripts/XSS.
 */
export function sanitizeBlogHtml(dirty: string | null | undefined): string {
  if (!dirty || typeof dirty !== 'string') return '';
  registerLinkTargetHook();

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'span',
      'div',
      'section',
      'article',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'strike',
      'sub',
      'sup',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'pre',
      'code',
      'a',
      'img',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'colgroup',
      'col',
      'hr',
      'figure',
      'figcaption',
    ],
    ALLOWED_ATTR: [
      'href',
      'title',
      'target',
      'rel',
      'name',
      'src',
      'alt',
      'width',
      'height',
      'class',
      'id',
      'colspan',
      'rowspan',
      'align',
      'style',
    ],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
  });
}

/** True if Quill/HTML content has no meaningful text (empty post). */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html || !html.trim()) return true;
  if (typeof document === 'undefined') {
    const stripped = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim();
    return stripped.length === 0;
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (div.textContent || '').replace(/\u00a0/g, ' ').trim();
  return text.length === 0;
}
